import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FileInputProps } from "../../types/screensTypes";
import Button from "../ui/Button";
import styles from "./FileInput.module.css";

export const FileInput: React.FC<FileInputProps> = ({
    onFileSelected,
    placeholder,
    showButton = false,
    buttonText,
    isMissing = false,
    multiple = false, // Allow multiple files if needed
    acceptedFileTypes,
    maxSize,
    maxSizeLabel,
    disabled = false,
    buttonVariant = "outline",
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
        onDrop: (acceptedFiles: File[]) => {
            setErrorMessage("");
            if (acceptedFiles.length > 0) {
                onFileSelected(multiple ? acceptedFiles : acceptedFiles[0]);
            }
        },
        multiple: multiple,
        accept: acceptedFileTypes,
        maxSize: maxSize,
        disabled: disabled,
        onDropRejected: (fileRejections) => {
            const firstError = fileRejections[0]?.errors[0];
            if (firstError?.code === "file-too-large") {
                setErrorMessage(maxSizeLabel ? t("imageExceedsMaxSize", { maxSize: maxSizeLabel }) : t("fileTooLarge"));
            } else if (firstError?.code === "file-invalid-type") {
                setErrorMessage(t("unsupportedFileType"));
            } else {
                setErrorMessage(t("fileUploadFail"));
            }
        },
    });

    const text = placeholder && placeholder !== "" ? placeholder : t("dragAndDrop");
    const btnText = buttonText && buttonText !== "" ? buttonText : t("upload");

    const className = isMissing
        ? `${styles.dropzone} ${styles.dropzoneMissing} file-input-container input-error`
        : `${styles.dropzone} file-input-container`;

    return (
        <div data-rw-control="file-input">
            <div {...getRootProps()} className={className} data-rw-part="dropzone">
                <input {...getInputProps()} />
                <p>{text}</p>

                {acceptedFiles.length > 0 && (
                    <div className={`${styles.selectedFile} selected-file`} data-rw-part="selected">
                        {multiple ? (
                            <ul>
                                {acceptedFiles.map((file, index) => (
                                    <li key={index}>
                                        <strong>{t("fileWithSemicolon")}</strong> {file.name}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <>
                                <strong>{t("fileWithSemicolon")}</strong> {acceptedFiles[0].name}
                            </>
                        )}
                    </div>
                )}
            </div>

            {errorMessage && (
                <p className={`${styles.error} upload-error-message`} data-rw-part="error" role="alert">
                    {errorMessage}
                </p>
            )}

            {showButton && (
                <div className={styles.buttonWrap}>
                    <Button
                        className={buttonVariant === "outline" ? "btn-outline" : `${buttonVariant}-button`}
                        label={btnText}
                        onClick={open}
                    />
                </div>
            )}
        </div>
    );
};
